/**
 * CDS-6008 — transitional adapter.
 *
 * The ETS threat scores used to arrive over the `query-new/*` WebSocket frames,
 * which basal-ganglia served by reading InfluxDB. That polling is what melted
 * Influx v1 in the incident this ticket responds to.
 *
 * These hooks fetch the same numbers from warning-and-scoring over REST and
 * dispatch them into the existing Redux slices, unchanged. Every selector and
 * every component that reads `wasValue`, `wasTrend` and
 * `latestLocationThreatScores` keeps working with no edit.
 *
 * This is deliberately a seam, not a destination: React Query wrapping a Redux
 * dispatch is not the pattern this codebase is moving towards. It exists so the
 * Influx dependency could be cut without touching ~42 consumer files on pages
 * that are being retired. When those pages go, delete this file and the slices
 * with them.
 */
import {useEffect} from 'react'
import {useDispatch} from 'react-redux'
import * as Sentry from '@sentry/react'
import {
    useQueryLatestFleetThreatScore,
    useQueryFleetThreatScore,
    useQueryLatestLocationThreatScores,
    useQueryLocationThreatScore,
    useQueryLocationTagThreatScore,
} from '../../query/warning-and-scoring/threat-scores'
import type {
    ThreatScorePoint,
    ThreatScorePeriod,
    LocationThreatScoreSeries,
    FleetThreatScoreSeries,
} from '../../api/warning-and-scoring/types/warning-and-scoring.types'
import {
    wasRequestFailedAction,
    receiveWasAction,
    requestWasAction,
} from './was-value/action-creators'
import {
    receiveTrendAction,
    requestTrendAction,
    trendRequestFailedAction,
} from './was-trend/action-creators'
import {
    latestLocationThreatScoresRequestFailed,
    receiveLatestLocationThreatScores,
    requestLatestLocationThreatScores,
} from './latest-location-threat-scores/action-creators'
import {
    locationTagThreatScoreRequestFailed,
    receiveLocationTagThreatScore,
    requestLocationTagThreatScore,
} from './location-tag-score/action-creators'
import type {LatestLocationThreatScores} from './latest-location-threat-scores/state'
import type {WasTrendScore} from '../../values/was-trend-score/WasTrendScore'
import {
    LAST_12H,
    LAST_24H,
    LAST_7D,
    LAST_30D,
    LAST_90D,
    type TimeRange,
} from '../../values/TimeRange'

/**
 * The five trend ranges, each paired with the `timePeriod` the WaS trend
 * endpoints want for it.
 *
 * This used to be a `TimeRange[]` plus a `toThreatScorePeriod` helper that cast
 * `timeRange.toString()` to `ThreatScorePeriod`. The cast was unsound:
 * `TimeRange` also has `LAST_1H`, which is not a `ThreatScorePeriod`, so a
 * caller could compile while sending a `timePeriod` the server 400s on. Writing
 * the pairs out means the period side is checked by the compiler and an invalid
 * one cannot be constructed. That matters more than usual here, because the
 * `--12h` bug in trap 4.1 was this exact class and it shipped.
 *
 * Do not add the leading hyphen of Go's `scoring.Period`. The handlers add it
 * themselves and sending it produces `--12h` and a 400. See ThreatScorePeriod.
 */
const TREND_RANGES: {timeRange: TimeRange; period: ThreatScorePeriod}[] = [
    {timeRange: LAST_12H, period: '12h'},
    {timeRange: LAST_24H, period: '24h'},
    {timeRange: LAST_7D, period: '7d'},
    {timeRange: LAST_30D, period: '30d'},
    {timeRange: LAST_90D, period: '90d'},
]

/**
 * WaS timestamps are RFC3339 strings; the slices expect epoch milliseconds,
 * which is what the WebSocket payloads used to carry.
 */
function toEpochMs(timestamp: string): number {
    const parsed = Date.parse(timestamp)
    return Number.isNaN(parsed) ? 0 : parsed
}

function toTrendScores(
    series: FleetThreatScoreSeries | LocationThreatScoreSeries,
): WasTrendScore[] {
    return series.items.map((point) => ({
        time: toEpochMs(point.timestamp),
        score: point.score,
    }))
}

/**
 * A score query that keeps failing is otherwise invisible: the slices have no
 * error field and nothing renders one, so the only symptom is a widget that
 * never leaves its loading state. That is exactly how the `--12h` period bug
 * hid during development.
 */
function reportScoreQueryFailure(query: string, error: unknown): void {
    Sentry.captureException(error, {tags: {ticket: 'CDS-6008', threatScoreQuery: query}})
}

/**
 * Fleet or single-vessel current score -> the `wasValue` slice.
 *
 * Mirrors the old WebSocket thunk: fleet endpoint when no location
 * is active, otherwise the score for that vessel out of the locations map.
 *
 * One effect, driven by the query's status. Three things depend on that shape:
 *
 *   - `request*` fires on `isInitialLoading` AND on the query never having
 *     settled, so a 2 minute background poll does not swap a drawn widget for a
 *     spinner. Both halves are load-bearing, see FIRST LOAD below.
 *   - the receive branch is keyed on `dataUpdatedAt`, not on the identity of
 *     `data`. React Query's structural sharing hands back the previous object
 *     when a refetch returns identical JSON, which it does for most of every 5
 *     minute server cache generation, so an identity-keyed effect would never
 *     re-run and never clear the loading flag.
 *   - an error clears the loading flag and leaves the slice's data alone,
 *     rather than leaving the widget spinning with nothing reported.
 *
 * FIRST LOAD. Every `request*` branch in this file is guarded by
 *
 *     isInitialLoading && dataUpdatedAt === 0 && errorUpdatedAt === 0
 *
 * rather than by `isInitialLoading` alone, which is what it used to be and which
 * was wrong. query-core's `fetch` reducer case does
 *
 *     ...(!state.dataUpdatedAt && {error: null, status: 'loading'})
 *
 * (query-core/build/lib/query.js:391-394), so a query that has never succeeded
 * is put back to `status: 'loading'` at the start of every poll, which makes
 * `isInitialLoading` true again. On `isInitialLoading` alone that re-dispatches
 * `request*`, and while warning-and-scoring is down the My Fleet and Risk Score
 * Over Time widgets drop back to a spinner on every 2 minute poll, for as long
 * as the outage lasts. `dataUpdatedAt` and `errorUpdatedAt` only ever move
 * forward, so they latch and the recurrence stops.
 *
 * `isInitialLoading` still has to be in the test. A disabled query has also
 * never settled, and without it a query that is switched off would report itself
 * as loading forever. See the tests in
 * `_tests_/was-warning-and-scoring-adapter.test.tsx`.
 */
function useWasValue(location: string | null): void {
    const dispatch = useDispatch()
    const isFleetView = location == null

    const fleet = useQueryLatestFleetThreatScore({enabled: isFleetView})
    const locations = useQueryLatestLocationThreatScores({enabled: !isFleetView})

    const query = isFleetView ? fleet : locations
    const {status, isInitialLoading, error, dataUpdatedAt, errorUpdatedAt} = query

    const point: ThreatScorePoint | undefined = isFleetView
        ? fleet.data
        : location
          ? locations.data?.[location]
          : undefined

    useEffect(() => {
        // See FIRST LOAD on this hook's doc comment for why the two zero checks
        // are here and not just `isInitialLoading`.
        if (isInitialLoading && dataUpdatedAt === 0 && errorUpdatedAt === 0) {
            dispatch(requestWasAction())
            return
        }
        if (status === 'error') {
            reportScoreQueryFailure(
                isFleetView ? 'latestFleetThreatScore' : 'latestLocation',
                error,
            )
            dispatch(wasRequestFailedAction())
            return
        }
        // Resolved, but this vessel has no entry in the map. Not the same as
        // still loading, and not an error either - the request succeeded.
        // medulla-ui's vessel list and warning-and-scoring's own `locations`
        // table are separately synced copies that can disagree, so a vessel the
        // UI can select may have no score yet. Without this branch
        // `requestWasAction` is never undone and the widget spins forever, or
        // keeps showing the previously selected vessel's score.
        if (status === 'success' && !point) {
            dispatch(wasRequestFailedAction())
            return
        }
        if (!point) {
            return
        }
        // highScore/mediumScore/lowScore are part of WasData but nothing reads
        // them; WaS does not provide a band breakdown.
        dispatch(
            receiveWasAction({
                score: point.score,
                highScore: 0,
                mediumScore: 0,
                lowScore: 0,
                timestamp: toEpochMs(point.timestamp),
            }),
        )
    }, [
        dispatch,
        isFleetView,
        isInitialLoading,
        status,
        error,
        dataUpdatedAt,
        errorUpdatedAt,
        point,
    ])
}

/** One trend range -> the `wasTrend` slice. See useWasValue for the effect shape. */
function useWasTrendRange(
    timeRange: TimeRange,
    period: ThreatScorePeriod,
    location: string | null,
): void {
    const dispatch = useDispatch()
    const isFleetView = location == null

    const fleet = useQueryFleetThreatScore(period, {enabled: isFleetView})
    const single = useQueryLocationThreatScore(period, location, {enabled: !isFleetView})

    const query = isFleetView ? fleet : single
    const {status, isInitialLoading, error, dataUpdatedAt, errorUpdatedAt} = query
    const series = query.data

    useEffect(() => {
        // See FIRST LOAD on useWasValue.
        if (isInitialLoading && dataUpdatedAt === 0 && errorUpdatedAt === 0) {
            dispatch(requestTrendAction(timeRange))
            return
        }
        if (status === 'error') {
            reportScoreQueryFailure(isFleetView ? 'fleetThreatScore' : 'locationThreatScore', error)
            dispatch(trendRequestFailedAction(timeRange))
            return
        }
        if (!series) {
            return
        }
        dispatch(receiveTrendAction(timeRange, toTrendScores(series)))
    }, [
        dispatch,
        timeRange,
        isFleetView,
        isInitialLoading,
        status,
        error,
        dataUpdatedAt,
        errorUpdatedAt,
        series,
    ])
}

/**
 * Replaces the six WebSocket registrations the Dashboard and the Operational
 * view each used to make: one current score plus five trend ranges.
 * Call once per page, passing the active location (null for fleet view).
 */
export function useWasScores(location: string | null): void {
    useWasValue(location)
    // Fixed-length list, so the hook order is stable across renders.
    useWasTrendRange(TREND_RANGES[0].timeRange, TREND_RANGES[0].period, location)
    useWasTrendRange(TREND_RANGES[1].timeRange, TREND_RANGES[1].period, location)
    useWasTrendRange(TREND_RANGES[2].timeRange, TREND_RANGES[2].period, location)
    useWasTrendRange(TREND_RANGES[3].timeRange, TREND_RANGES[3].period, location)
    useWasTrendRange(TREND_RANGES[4].timeRange, TREND_RANGES[4].period, location)
}

/**
 * One tag trend range -> the `locationTagScore` slice. See useWasValue
 * for the effect shape.
 */
function useLocationTagTrendRangeFromWaS(
    timeRange: TimeRange,
    period: ThreatScorePeriod,
    tag: string | null,
): void {
    const dispatch = useDispatch()
    const {data, status, isInitialLoading, error, dataUpdatedAt, errorUpdatedAt} =
        useQueryLocationTagThreatScore(period, tag)

    useEffect(() => {
        // See FIRST LOAD on useWasValue.
        if (isInitialLoading && dataUpdatedAt === 0 && errorUpdatedAt === 0) {
            dispatch(requestLocationTagThreatScore(timeRange))
            return
        }
        if (status === 'error') {
            reportScoreQueryFailure('locationTagThreatScore', error)
            dispatch(locationTagThreatScoreRequestFailed(timeRange))
            return
        }
        if (!data) {
            return
        }
        dispatch(
            receiveLocationTagThreatScore(
                timeRange,
                data.items.map((point) => ({
                    time: toEpochMs(point.timestamp),
                    tagScore: point.score,
                })),
            ),
        )
    }, [dispatch, timeRange, isInitialLoading, status, error, dataUpdatedAt, errorUpdatedAt, data])
}

/**
 * Replaces the five WebSocket registrations the Fleet Timeline widget made when
 * a vessel tag was searched. Pass null when no tag is selected; the underlying
 * queries stay disabled until one is.
 */
export function useLocationTagScoresFromWarningAndScoring(tag: string | null): void {
    useLocationTagTrendRangeFromWaS(TREND_RANGES[0].timeRange, TREND_RANGES[0].period, tag)
    useLocationTagTrendRangeFromWaS(TREND_RANGES[1].timeRange, TREND_RANGES[1].period, tag)
    useLocationTagTrendRangeFromWaS(TREND_RANGES[2].timeRange, TREND_RANGES[2].period, tag)
    useLocationTagTrendRangeFromWaS(TREND_RANGES[3].timeRange, TREND_RANGES[3].period, tag)
    useLocationTagTrendRangeFromWaS(TREND_RANGES[4].timeRange, TREND_RANGES[4].period, tag)
}

/**
 * Replaces the WebSocket registration that used to run for the whole
 * authenticated session from `fetchInitialData()`. Feeds the
 * per-vessel scores consumed across My Vessels, the dashboards
 * and the inventory modals. Mount once, high in the authenticated tree.
 */
export function useLatestLocationThreatScoresFromWarningAndScoring(): void {
    const dispatch = useDispatch()
    const {data, status, isInitialLoading, error, dataUpdatedAt, errorUpdatedAt} =
        useQueryLatestLocationThreatScores()

    useEffect(() => {
        // See FIRST LOAD on useWasValue.
        if (isInitialLoading && dataUpdatedAt === 0 && errorUpdatedAt === 0) {
            dispatch(requestLatestLocationThreatScores())
            return
        }
        if (status === 'error') {
            reportScoreQueryFailure('latestLocationThreatScores', error)
            dispatch(latestLocationThreatScoresRequestFailed())
            return
        }
        if (!data) {
            return
        }
        const scores: LatestLocationThreatScores = {}
        for (const [location, point] of Object.entries(data)) {
            scores[location] = {
                score: point.score,
                timestamp: toEpochMs(point.timestamp),
            }
        }
        dispatch(receiveLatestLocationThreatScores(scores))
    }, [dispatch, isInitialLoading, status, error, dataUpdatedAt, errorUpdatedAt, data])
}
